import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const faqs = [
  {
    category: "General",
    items: [
      {
        q: "What is Trinity?",
        a: "Trinity is an AI-enabled platform built by Eternity Solutions to help tech-services leaders capture their journey, generate insight, and execute with clarity on their growth priorities.",
      },
      {
        q: "Who is Trinity designed for?",
        a: "Trinity is designed for founders, CEOs, revenue leaders, and key executives in tech-services and related businesses who want to improve their thinking, planning, and execution performance.",
      },
      {
        q: "How is Trinity related to Eternity?",
        a: "Eternity Solutions is the company behind Trinity. Eternity focuses on growth advisory and leadership engagements, while Trinity is the AI-powered platform that brings these ideas into daily execution.",
      },
    ],
  },
  {
    category: "Using the Platform",
    items: [
      {
        q: "How do I get started with Trinity?",
        a: "You can start by reaching out through the contact form on our website. We will set up an onboarding conversation, understand your context, and help you get started with the most relevant Trinity workflows.",
      },
      {
        q: "What can I do inside Trinity?",
        a: "You can capture your reflections, define missions and goals, create action plans, connect meetings, and use AI-guided prompts to bring more clarity, focus, and follow-through to your work.",
      },
    ],
  },
  {
    category: "Data & Privacy",
    items: [
      {
        q: "Is my data secure?",
        a: "We take data security seriously and use industry-standard measures such as encryption in transit, secure storage, and role-based access to help protect your information.",
      },
      {
        q: "Do you use my data to train AI models?",
        a: "We do not permit third-party AI providers to use your data to train their general-purpose models. Your data is processed only to provide and improve the Trinity experience.",
      },
      {
        q: "Can I request deletion of my data?",
        a: "Yes. You or your organization can request deletion of your account and associated data, subject to any legal or contractual obligations we may have.",
      },
    ],
  },
  {
    category: "Support & Billing",
    items: [
      {
        q: "How do I get support?",
        a: "You can contact us via email or through the contact section of the website. We aim to respond as promptly as possible based on the nature of your request.",
      },
      {
        q: "How does pricing work?",
        a: "Pricing may vary based on whether you are using Trinity as an individual or as part of a cohort or organization. We will share details during our discovery and onboarding conversation.",
      },
    ],
  },
];

export default function EtFAQ() {
  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose - FAQ page section for Eternity / Trinity website

  const [openItem, setOpenItem] = useState(null);

  const toggleItem = (key) => {
    setOpenItem((prev) => (prev === key ? null : key));
  };

  return (
    <section
      id="faq"
      className="min-h-screen bg-black text-gray-200 py-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-5xl mx-auto">
        {/* Heading */}
        <motion.header
          className="mb-10 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-widest text-orange-400 mb-2">
            Help
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-orange-500 mb-3">
            Frequently Asked Questions
          </h1>
          <p className="text-sm md:text-base text-gray-400 max-w-2xl mx-auto">
            A few quick answers to common questions about Trinity, Eternity, and
            how we work with you.
          </p>
        </motion.header>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqs.map((group, groupIndex) => (
            <motion.div
              key={group.category}
              className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-5 sm:p-6"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: groupIndex * 0.1 }}
            >
              <h2 className="text-lg md:text-xl font-semibold text-orange-400 mb-4">
                {group.category}
              </h2>
              <div className="divide-y divide-white/10">
                {group.items.map((item, itemIndex) => {
                  const key = `${groupIndex}-${itemIndex}`;
                  const isOpen = openItem === key;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleItem(key)}
                      className="w-full text-left py-3 flex items-start justify-between gap-4 focus:outline-none"
                    >
                      <div>
                        <p className="font-medium text-gray-100">
                          {item.q}
                        </p>
                        {isOpen && (
                          <p className="mt-2 text-sm md:text-base text-gray-300">
                            {item.a}
                          </p>
                        )}
                      </div>
                      <ChevronDownIcon
                        className={`mt-1 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom hint */}
        <div className="mt-10 text-center text-sm text-gray-400">
          Still have questions?{" "}
          <a
            href="#contact"
            className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
          >
            Reach out to us
          </a>{" "}
          and we&apos;ll be happy to explore your context.
        </div>
      </div>
    </section>
  );
}